import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLoadingState } from '../../hooks';
import { LoadingPanel } from '../brand/TwindenLoader';
import {
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Plus, Trash2 } from 'lucide-react';
import { adminApi, ApiError, type CostMonth } from '../../lib/api';
import { DEFAULT_COST_ITEMS, formatMoney, monthName } from '../../content/site';

// The system root keeps Twinden's books here: each month's costs and what
// families paid (M43, docs/costs-and-contributions.md D109–D111). The
// landing and pricing pages show them.

type Draft = Omit<CostMonth, 'month'>;

function thisMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** A new month starts from the latest earlier month, or the defaults (D110). */
function startingDraft(months: CostMonth[], month: string): Draft {
  const previous = [...months]
    .filter((row) => row.month < month)
    .sort((a, b) => b.month.localeCompare(a.month))[0];
  return {
    received: 0,
    familiesCount: null,
    estimated: true,
    note: null,
    items: previous
      ? previous.items.map((item) => ({ ...item }))
      : DEFAULT_COST_ITEMS.map((item) => ({
          label: item.label,
          detail: item.detail || null,
          amount: 0,
          upcoming: false,
        })),
  };
}

function Amount({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
}) {
  return (
    <NumberInput
      min={0}
      value={value}
      onChange={(_, number) =>
        onChange(Number.isNaN(number) ? 0 : Math.round(number))
      }
      w="140px"
    >
      <NumberInputField aria-label={label} placeholder="₹" />
    </NumberInput>
  );
}

/** Families who chipped in (D196): a whole number, or empty = not counted. */
function FamiliesCount({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  return (
    <NumberInput
      min={0}
      value={value ?? ''}
      onChange={(text, number) =>
        onChange(
          text === '' || Number.isNaN(number) ? null : Math.round(number)
        )
      }
      w="140px"
    >
      <NumberInputField
        aria-label="Families who chipped in"
        placeholder="Families"
      />
    </NumberInput>
  );
}

export function CostsTab() {
  const toast = useToast();
  const [months, setMonths] = useState<CostMonth[]>([]);
  const [month, setMonth] = useState(thisMonth());
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<unknown>(null);

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const response = await adminApi.getCosts();
      setMonths(response.data);
      return response.data;
    } catch (err) {
      setFailed(err);
      return null;
    }
  }, []);

  const loadFirst = useCallback(() => {
    void load().then((rows) => {
      if (!rows) return;
      const existing = rows.find((row) => row.month === thisMonth());
      setDraft(existing ? { ...existing } : startingDraft(rows, thisMonth()));
    });
  }, [load]);

  useEffect(() => {
    loadFirst();
  }, [loadFirst]);

  const choose = (value: string) => {
    setMonth(value);
    const existing = months.find((row) => row.month === value);
    setDraft(existing ? { ...existing } : startingDraft(months, value));
  };

  const options = useMemo(() => {
    const keys = new Set(months.map((row) => row.month));
    keys.add(thisMonth());
    keys.add(month);
    return [...keys].sort().reverse();
  }, [months, month]);

  const loadingState = useLoadingState({
    loading: !draft && !failed,
    hasData: draft !== null,
    error: failed,
    retry: loadFirst,
  });
  if (!draft || loadingState.show) return <LoadingPanel state={loadingState} />;

  const total = draft.items
    .filter((item) => !item.upcoming)
    .reduce((sum, item) => sum + item.amount, 0);
  const saved = months.some((row) => row.month === month);

  const updateItem = (index: number, change: Partial<Draft['items'][number]>) =>
    setDraft({
      ...draft,
      items: draft.items.map((item, i) =>
        i === index ? { ...item, ...change } : item
      ),
    });

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.saveCostMonth(month, {
        ...draft,
        items: draft.items.filter((item) => item.label.trim()),
      });
      await load();
      toast({ title: `${monthName(month)} published`, status: 'success' });
    } catch (err) {
      toast({
        title: 'Could not save',
        description:
          err instanceof ApiError ? err.message : 'Something went wrong',
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await adminApi.deleteCostMonth(month);
      const rows = await load();
      setDraft(startingDraft(rows ?? [], month));
      toast({ title: `${monthName(month)} removed`, status: 'success' });
    } catch (err) {
      toast({
        title: 'Could not remove',
        description:
          err instanceof ApiError ? err.message : 'Something went wrong',
        status: 'error',
      });
    }
  };

  return (
    <VStack align="stretch" spacing={5} maxW="760px">
      <Text color="muted">
        What Twinden costs each month and what families paid. The landing and
        pricing pages show the latest month up to today, the balance carried
        over, and how far ahead it pays. Amounts are whole rupees; items at ₹0
        aren’t shown publicly.
      </Text>

      <HStack spacing={3} flexWrap="wrap">
        <Select
          value={month}
          onChange={(e) => choose(e.target.value)}
          maxW="220px"
          aria-label="Month"
        >
          {options.map((key) => (
            <option key={key} value={key}>
              {monthName(key)}
              {months.some((row) => row.month === key) ? '' : ' (new)'}
            </option>
          ))}
        </Select>
        <Input
          type="month"
          maxW="200px"
          aria-label="Another month"
          onChange={(e) => e.target.value && choose(e.target.value)}
        />
      </HStack>

      <Box
        bg="surface"
        borderWidth="1px"
        borderColor="line"
        borderRadius="card"
        p={4}
      >
        <Text fontWeight={800} pb={3}>
          Costs
        </Text>
        <VStack align="stretch" spacing={3}>
          {draft.items.map((item, index) => (
            <HStack key={index} align="flex-start" spacing={2} flexWrap="wrap">
              <VStack align="stretch" spacing={1} flex="1" minW="220px">
                <Input
                  value={item.label}
                  placeholder="What it is"
                  aria-label="Item"
                  maxLength={60}
                  onChange={(e) => updateItem(index, { label: e.target.value })}
                />
                <Input
                  size="sm"
                  value={item.detail ?? ''}
                  placeholder="Detail (optional)"
                  aria-label="Detail"
                  maxLength={200}
                  onChange={(e) =>
                    updateItem(index, { detail: e.target.value || null })
                  }
                />
              </VStack>
              <Amount
                label={`${item.label || 'Item'} amount`}
                value={item.amount}
                onChange={(amount) => updateItem(index, { amount })}
              />
              <Checkbox
                pt={2}
                isChecked={item.upcoming}
                onChange={(e) =>
                  updateItem(index, { upcoming: e.target.checked })
                }
              >
                Upcoming
              </Checkbox>
              <IconButton
                aria-label={`Remove ${item.label || 'item'}`}
                icon={<Trash2 size={16} />}
                variant="ghost"
                colorScheme="gray"
                onClick={() =>
                  setDraft({
                    ...draft,
                    items: draft.items.filter((_, i) => i !== index),
                  })
                }
              />
            </HStack>
          ))}
          <Button
            alignSelf="flex-start"
            size="sm"
            variant="ghost"
            colorScheme="gray"
            leftIcon={<Plus size={16} />}
            onClick={() =>
              setDraft({
                ...draft,
                items: [
                  ...draft.items,
                  { label: '', detail: null, amount: 0, upcoming: false },
                ],
              })
            }
          >
            Add a cost
          </Button>
          <Text fontWeight={700}>Costs this month: {formatMoney(total)}</Text>
        </VStack>
      </Box>

      <Box
        bg="surface"
        borderWidth="1px"
        borderColor="line"
        borderRadius="card"
        p={4}
      >
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between" flexWrap="wrap">
            <Text fontWeight={800}>Paid by families this month</Text>
            <Amount
              label="Paid by families this month"
              value={draft.received}
              onChange={(received) => setDraft({ ...draft, received })}
            />
          </HStack>
          <HStack justify="space-between" flexWrap="wrap">
            <Text fontWeight={800}>Families who chipped in</Text>
            <FamiliesCount
              value={draft.familiesCount ?? null}
              onChange={(familiesCount) =>
                setDraft({ ...draft, familiesCount })
              }
            />
          </HStack>
          <Text fontSize="sm" color="muted">
            The month’s total from the Razorpay dashboard, and how many families
            it came from (leave empty if you haven’t counted). Only these totals
            are stored, never who paid or how much each family gave. The landing
            page and the chats’ thank-you card show them.
          </Text>
          <Checkbox
            isChecked={draft.estimated}
            onChange={(e) =>
              setDraft({ ...draft, estimated: e.target.checked })
            }
          >
            These are estimates (shown as “Estimated”)
          </Checkbox>
          <Textarea
            value={draft.note ?? ''}
            placeholder="A note for this month (optional), e.g. why costs changed"
            maxLength={500}
            onChange={(e) =>
              setDraft({ ...draft, note: e.target.value || null })
            }
          />
        </VStack>
      </Box>

      <HStack spacing={3}>
        <Button onClick={() => void save()} isLoading={saving}>
          {saved ? 'Save changes' : `Publish ${monthName(month)}`}
        </Button>
        {saved && (
          <Button
            variant="ghost"
            colorScheme="gray"
            color="danger"
            onClick={() => void remove()}
          >
            Remove this month
          </Button>
        )}
      </HStack>
    </VStack>
  );
}
